"use client"

import { useEffect, useRef, useState } from "react"
import { shouldAutoplayHeroVideo } from "./heroVideoPolicy"

interface NetworkInformationLike {
  saveData?: boolean
  effectiveType?: string
}

type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number
  cancelIdleCallback?: (handle: number) => void
}

/**
 * Video decorativo del panel de marca, diferido (S5-4 · P1-17).
 *
 * El HTML del servidor solo trae el `<video>` con `preload="none"` y un póster
 * de pocos KB: sin `src` ni `autoplay`, el navegador no descarga nada del mp4.
 * El `src` se asigna después del `load` de la página y de un momento ocioso —
 * cuando el formulario ya se pintó — y solo si `shouldAutoplayHeroVideo`.
 */
export function AuthHeroVideo({ src, poster, className }: { src: string; poster: string; className?: string }) {
  const ref = useRef<HTMLVideoElement>(null)
  const [active, setActive] = useState(false)

  useEffect(() => {
    const connection = (navigator as Navigator & { connection?: NetworkInformationLike }).connection
    const reducedMotion =
      typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (
      !shouldAutoplayHeroVideo({
        reducedMotion,
        saveData: connection?.saveData,
        effectiveType: connection?.effectiveType,
      })
    ) {
      return
    }

    const w = window as IdleWindow
    let idleHandle: number | undefined
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined
    const start = () => setActive(true)
    const schedule = () => {
      if (typeof w.requestIdleCallback === "function") idleHandle = w.requestIdleCallback(start, { timeout: 3000 })
      else timeoutHandle = setTimeout(start, 1500)
    }

    if (document.readyState === "complete") schedule()
    else window.addEventListener("load", schedule, { once: true })

    return () => {
      window.removeEventListener("load", schedule)
      if (idleHandle !== undefined) w.cancelIdleCallback?.(idleHandle)
      if (timeoutHandle !== undefined) clearTimeout(timeoutHandle)
    }
  }, [])

  useEffect(() => {
    if (!active) return
    // play() rechaza si el navegador bloquea el autoplay: se queda el póster.
    ref.current?.play().catch(() => {})
  }, [active])

  return (
    <video
      ref={ref}
      muted
      loop
      playsInline
      preload="none"
      poster={poster}
      src={active ? src : undefined}
      aria-hidden="true"
      tabIndex={-1}
      className={className}
    />
  )
}
